/* @flow */
import moment from 'moment';
import * as React from 'react';
import { translate } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import type { TranslatorProps } from 'react-i18next';

import Form from 'forms/_.extend';
import CaaSForm from '../CaaSDetails/CaaSForm';
import Loading from 'components/Loading/Loading';
import CaaSFormError from '../CaaSDetails/CaaSFormError';
import CaaSFormSuccess from '../CaaSDetails/CaaSFormSuccess';
import { actionDrawerStore } from 'services/ServiceRegistry';
import type TypeActionDrawerStore from 'stores/ActionDrawerStore';
import { CAAS_ACTIONS, ADDRESS_OPTS, CONTACT_OPTS } from '../CaaSDetails/constants';
import ActionDrawerHeader from 'views/ActionDrawer/components/ActionDrawerHeader/ActionDrawerHeader';

import styles from '../../../ActionDrawer.module.scss';

type TypeProps = {
  t: TranslatorProps,
  actionDrawerStore: TypeActionDrawerStore,
  assetStore: Object,
};

@translate()
@inject('actionDrawerStore', 'assetStore')
@observer
export default class RequestCaasDetailsMultiple extends React.Component<*> {
  componentWillUnmount() {
    this.props.assetStore.clearDetails();
  }

  createForm = () => {
    const { t, assetStore }: TypeProps = this.props;
    const assets: Object = this.props.formData;
    const activeUser: Object = JSON.parse(localStorage.getItem('user'));

    // All CaaS Actions must be visible for System Admin or Asset Manager roles.
    const isCaaSAdminRole = activeUser.role_id === 9 || activeUser.role_id === 7;

    const fields: Array<Object> = [
      {
        name: 'caasDetails',
        label: 'CaaS Details',
        fields: [
          {
            name: 'workOrderNumber',
            label: 'Work Order Number',
            rules: 'string|min:8|max:32',
            value: null,
          },
          {
            name: 'collection_address',
            label: 'Custom Address',
            rules: 'string',
            value: null,
          },
          {
            name: 'collectionDate',
            label: t('general:assets.labels.date_available_for_collection'),
            rules: `required|date`,
            value: new Date(
              moment()
                .add(1, 'd')
                .format('YYYY-MM-DD')
            ),
          },
          {
            name: 'completionDate',
            label: 'Close Date',
            rules: `required|date`,
            value: new Date(
              moment()
                .add(1, 'd')
                .format('YYYY-MM-DD')
            ),
          },
          {
            name: 'contact_name',
            label: 'Custom Name',
            rules: 'string',
            value: null,
          },
          {
            name: 'contact_phone',
            label: 'Custom Phone',
            rules: 'telephone',
            value: null,
          },
        ],
      },
    ];

    if (isCaaSAdminRole) {
      fields[0].fields.push({
        name: 'caas_actions',
        label: 'CaaS Actions',
        value: CAAS_ACTIONS.caas_schedule.name,
        options: [
          { id: CAAS_ACTIONS.caas_schedule.id, name: CAAS_ACTIONS.caas_schedule.name },
          { id: CAAS_ACTIONS.caas_updates.id, name: CAAS_ACTIONS.caas_updates.name },
          { id: CAAS_ACTIONS.caas_completion.id, name: CAAS_ACTIONS.caas_completion.name },
        ],
      });
    } else {
      fields[0].fields.push({
        name: 'caas_actions',
        label: 'CaaS Actions',
        value: CAAS_ACTIONS.caas_schedule.name,
        options: [{ id: CAAS_ACTIONS.caas_schedule.id, name: CAAS_ACTIONS.caas_schedule.name }],
      });
    }

    const hooks: Object = {
      onInit(data: Object) {
        if (assets.length > 0) {
          assetStore.loadAssets(assets.map(item => item.id));
        }
      },
      onSuccess(data: Object) {
        const formData = data.values().caasDetails;
        const assetList = assets.map(asset => asset.id);

        if (formData.caas_actions === CAAS_ACTIONS.caas_schedule.name) {
          actionDrawerStore.requestCaaSActions(CAAS_ACTIONS.caas_schedule.id, {
            assets_ids: assetList,
            use_asset_collection_address: false,
            collection_address: formData.collection_address,
            use_asset_contact: false,
            contact_name: formData.contact_name,
            contact_phone: formData.contact_phone,
            collection_date: moment(formData.collectionDate).format('YYYY-MM-DD'),
          });
        }

        if (formData.caas_actions === CAAS_ACTIONS.caas_updates.name) {
          actionDrawerStore.requestCaaSActions(CAAS_ACTIONS.caas_updates.id, {
            assets_ids: assetList,
            work_order_number: formData.workOrderNumber,
          });
        }

        if (formData.caas_actions === CAAS_ACTIONS.caas_completion.name) {
          actionDrawerStore.requestCaaSActions(CAAS_ACTIONS.caas_completion.id, {
            assets_ids: assetList,
            closure_date: moment(formData.completionDate).format('YYYY-MM-DD'),
          });
        }

        assetStore.clearDetails();
      },
    };
    return new Form(
      { fields },
      {
        hooks,
        name: 'CaaSDetailsMultiple',
      }
    );
  };

  render(): React.Element<*> {
    const { t, actionDrawerStore }: TypeProps = this.props;

    const assets: Object = this.props.formData;
    const title: string = 'Calibration as a Service';

    if (actionDrawerStore.loading) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={title} />
          <CaaSFormError />
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.success}`}>
          <ActionDrawerHeader title={title} />
          <CaaSFormSuccess />
        </div>
      );
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={title} />
        <CaaSForm renderMultiple={true} assets={assets} form={this.createForm()} />
      </div>
    );
  }
}
